import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { TunnelVariableValideurDto } from 'api/funnel';
import { funnelsAPI } from 'services/swagger-api';
import { usePanel } from 'hooks/usePanel';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { Checkbox } from 'components/forms/fragments/Checkbox';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { StripedList } from 'back-office/shared/components/BlockCard';
import { DASH } from '../funnelFormat';

interface ValidatorVariablesPanelProps {
  tunnelId: string;
  variables: TunnelVariableValideurDto[];
  onSuccess: () => void;
}

/** Side panel picking the tunnel variables the validator may modify (exact list saved in one call). */
export const ValidatorVariablesPanel: FC<ValidatorVariablesPanelProps> = ({
  tunnelId,
  variables,
  onSuccess,
}) => {
  const { setPanelActions, closePanel } = usePanel();

  const savedIds = useMemo(
    () =>
      new Set(
        variables
          .filter((variable) => variable.modifiableParValideur)
          .map((variable) => variable.variableValueId)
      ),
    [variables]
  );
  const [checked, setChecked] = useState<Set<string>>(() => new Set(savedIds));
  const [isSaving, setIsSaving] = useState(false);

  const allChecked = variables.length > 0 && variables.every((v) => checked.has(v.variableValueId));
  const someChecked = variables.some((v) => checked.has(v.variableValueId));
  const changed = checked.size !== savedIds.size || Array.from(checked).some((id) => !savedIds.has(id));

  const toggle = (variableValueId: string, value: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (value) next.add(variableValueId);
      else next.delete(variableValueId);
      return next;
    });

  const toggleAll = () =>
    setChecked(allChecked ? new Set() : new Set(variables.map((v) => v.variableValueId)));

  useEffect(() => {
    const failureMessage = "L'enregistrement des variables a échoué";
    setPanelActions({
      closeLabel: 'COMMON.CANCEL',
      submitLabel: 'COMMON.SAVE',
      submitDisabled: isSaving || !changed,
      onSubmit: async () => {
        setIsSaving(true);
        try {
          const response = await funnelsAPI.setFunnelValidatorVariables({
            tunnelId,
            setVariablesValideurCommand: { variableValueIds: Array.from(checked) },
          });
          if (!response.isSuccess) {
            toast.error(response.errorMessage || failureMessage);
            return;
          }
          toast.success('Variables ouvertes au valideur enregistrées');
          onSuccess();
          closePanel();
        } catch (err) {
          console.error(failureMessage, err);
          const parsed = await parseApiError(err);
          toast.error(parsed.message || failureMessage);
        } finally {
          setIsSaving(false);
        }
      },
    });
  }, [setPanelActions, closePanel, onSuccess, tunnelId, checked, changed, isSaving]);

  if (variables.length === 0) {
    return <p className="text-sm text-gray-400">Aucune variable sur ce tunnel.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-zinc-700">
          <strong className="font-bold text-zinc-800">{checked.size}</strong> sur{' '}
          {variables.length}
        </span>
        <button
          type="button"
          onClick={toggleAll}
          disabled={isSaving}
          className="inline-flex items-center gap-2 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-60"
        >
          {/* Presentational box: the button's onClick drives it, avoiding a double toggle. */}
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked && !allChecked}
            onChange={() => {}}
          />
          {allChecked ? 'Tout décocher' : 'Tout cocher'}
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200">
        <StripedList>
          {variables.map((variable) => (
            <li key={variable.variableValueId} className="flex items-center gap-3 px-4 py-2.5">
              <CheckboxComponent
                name={variable.variableValueId}
                label={variable.libelle || DASH}
                checked={checked.has(variable.variableValueId)}
                onChange={(value) => toggle(variable.variableValueId, value)}
                disabled={isSaving}
              />
              <code className="ml-auto truncate rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-zinc-600">
                {variable.code || DASH}
              </code>
            </li>
          ))}
        </StripedList>
      </div>
    </div>
  );
};
